import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowLeft,
  ArrowRight,
  Compass,
  MoonStar,
  Timer,
  Wind,
} from "lucide-react";

const TRANSITION_MS = 240;
const INTRO_DURATION_MS = 2500;

const NEED_OPTIONS = Object.freeze([
  {
    key: "relax",
    Icon: Wind,
    label: "Me détendre",
    description: "Relâcher la pression et retrouver du calme.",
  },
  {
    key: "rest",
    Icon: MoonStar,
    label: "Me reposer",
    description: "Ralentir et laisser davantage de place au repos.",
  },
  {
    key: "recenter",
    Icon: Compass,
    label: "Me recentrer",
    description: "Retrouver un point d’ancrage et plus de clarté.",
  },
]);

const DURATION_OPTIONS = Object.freeze([
  {
    value: "30sec",
    label: "30 secondes",
    description: "Commencer simplement.",
  },
  {
    value: "2min",
    label: "2 minutes",
    description: "Créer une vraie pause dans la journée.",
  },
  {
    value: "5min",
    label: "5 minutes",
    description: "Installer un rituel plus profond.",
  },
  {
    value: "10min",
    label: "10 minutes",
    description: "Prendre un temps durable pour vous.",
  },
]);

const NEED_FINAL_COPY = Object.freeze({
  relax: {
    opening: "Lorsque la pression monte",
    purpose: "vous aider à relâcher la tension et retrouver du calme",
  },
  rest: {
    opening: "Lorsque vous avez besoin de ralentir",
    purpose: "vous accompagner vers un rythme plus doux et plus reposant",
  },
  recenter: {
    opening: "Lorsque votre attention se disperse",
    purpose: "vous aider à retrouver de la clarté et un point d’ancrage",
  },
});

const DURATION_LABELS = Object.freeze(
  DURATION_OPTIONS.reduce((labels, option) => {
    labels[option.value] = option.label.toLowerCase();
    return labels;
  }, {}),
);

function PreferenceOption({
  Icon,
  label,
  description,
  selected,
  onClick,
}) {
  return (
    <button
      type="button"
      className={`onboarding-option ${selected ? "is-selected" : ""}`}
      onClick={onClick}
      aria-pressed={selected}
    >
      <span className="onboarding-option__icon" aria-hidden="true">
        <Icon size={22} strokeWidth={1.9} />
      </span>

      <span className="onboarding-option__copy">
        <strong>{label}</strong>
        <span>{description}</span>
      </span>

      <ArrowRight
        className="onboarding-option__arrow"
        size={19}
        strokeWidth={2}
        aria-hidden="true"
      />
    </button>
  );
}

export default function UserOnboarding({ onComplete }) {
  const [stage, setStage] = useState("intro");
  const [primaryNeed, setPrimaryNeed] = useState("");
  const [dailyTime, setDailyTime] = useState("");
  const [isLeaving, setIsLeaving] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const savingRef = useRef(false);

  const introTimerRef = useRef(null);
  const transitionTimerRef = useRef(null);
  const isLeavingRef = useRef(false);

  const selectedNeed = useMemo(
    () => NEED_OPTIONS.find((option) => option.key === primaryNeed) || null,
    [primaryNeed],
  );

  const finalCopy = NEED_FINAL_COPY[primaryNeed] || NEED_FINAL_COPY.relax;
  const durationLabel = DURATION_LABELS[dailyTime] || "2 minutes";

  useEffect(() => {
    document.body.classList.add("onboarding-mode");

    return () => {
      document.body.classList.remove("onboarding-mode");
    };
  }, []);

useEffect(() => {
  return () => {
    isLeavingRef.current = false;

    if (introTimerRef.current) {
      window.clearTimeout(introTimerRef.current);
    }

    if (transitionTimerRef.current) {
      window.clearTimeout(transitionTimerRef.current);
    }
  };
}, []);


const changeStage = useCallback((nextStage, updateState) => {
  if (isLeavingRef.current) return;

  isLeavingRef.current = true;
  setIsLeaving(true);

  if (transitionTimerRef.current) {
    window.clearTimeout(transitionTimerRef.current);
  }

  transitionTimerRef.current = window.setTimeout(() => {
    updateState?.();
    setStage(nextStage);

    isLeavingRef.current = false;
    setIsLeaving(false);
  }, TRANSITION_MS);
}, []);

  useEffect(() => {
    if (stage !== "intro") return undefined;

    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;

    introTimerRef.current = window.setTimeout(
      () => changeStage("need"),
      reduceMotion ? 1100 : INTRO_DURATION_MS,
    );

    return () => {
      if (introTimerRef.current) {
        window.clearTimeout(introTimerRef.current);
      }
    };
}, [stage, changeStage]);

  const chooseNeed = (option) => {
    changeStage("duration", () => {
      setPrimaryNeed(option.key);
    });
  };

  const chooseDuration = (value) => {
    changeStage("complete", () => {
      setDailyTime(value);
    });
  };

  const goBack = () => {
    if (stage !== "duration") return;
    changeStage("need");
  };

  const finishOnboarding = async () => {
    if (!selectedNeed || !dailyTime || savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    setSaveError("");
    try {
      if (typeof onComplete !== "function") throw new Error("Enregistrement indisponible.");
      await onComplete({ primaryNeed: selectedNeed.key, dailyTime, onboardingVersion: 3 });
    } catch (error) {
      console.warn("Onboarding non enregistré.", error);
      setSaveError("Vos choix n’ont pas pu être enregistrés. Vérifiez votre connexion et réessayez.");
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const questionStep = stage === "need" ? 1 : stage === "duration" ? 2 : null;
const questionCopy = stage === "duration"
  ? { eyebrow: "Votre rythme", title: "Combien de temps chaque jour ?", lead: "Ce choix devient votre objectif quotidien. Vos pauses s’additionnent pour l’atteindre." }
  : { eyebrow: "Calme et détente", title: "De quoi avez-vous envie ?", lead: "Choisissez l’intention qui vous ferait le plus de bien au quotidien." };

  return (
    <div className="onboarding-fullscreen" data-theme="light">
      <main
        className={`onboarding-experience ${isLeaving ? "is-leaving" : ""}`}
        aria-live="polite"
      >
        {stage === "intro" && (
          <section className="onboarding-cinematic onboarding-cinematic--intro">
<h1 className="onboarding-cinematic__title">
  Bienvenue dans votre refuge.
</h1>
          </section>
        )}

{(stage === "need" || stage === "duration") && (
  <section className="onboarding-question">
    <header className="onboarding-topbar">
      <div className="onboarding-topbar__side">
        {stage === "duration" ? (
          <button
            type="button"
            className="onboarding-back"
            onClick={goBack}
            aria-label="Revenir à la question précédente"
          >
            <ArrowLeft size={19} strokeWidth={2} aria-hidden="true" />
            <span>Retour</span>
          </button>
        ) : (
          <span className="onboarding-topbar__spacer" aria-hidden="true" />
        )}
      </div>

      <img
        src="/logo2.svg"
        alt="Harmonauria"
        className="onboarding-logo"
      />

      <div className="onboarding-topbar__side onboarding-topbar__side--right">
        <span className="onboarding-step-label">{questionStep} / 2</span>
      </div>
    </header>

    <div className="onboarding-progress" aria-hidden="true">
      <span style={{ width: questionStep === 1 ? "50%" : "100%" }} />
    </div>

    <header className="page-hero onboarding-page-hero">
      <p className="page-hero__eyebrow">{questionCopy.eyebrow}</p>
      <h1 className="page-hero__title">{questionCopy.title}</h1>
      <p className="page-hero__lead">{questionCopy.lead}</p>
    </header>

    <div className="onboarding-question__center">
      {stage === "need" && (
        <div className="onboarding-options">
          {NEED_OPTIONS.map((option) => (
            <PreferenceOption
              key={option.key}
              Icon={option.Icon}
              label={option.label}
              description={option.description}
              selected={primaryNeed === option.key}
              onClick={() => chooseNeed(option)}
            />
          ))}
        </div>
      )}

      {stage === "duration" && (
        <div className="onboarding-options onboarding-options--duration">
          {DURATION_OPTIONS.map((option) => (
            <PreferenceOption
              key={option.value}
              Icon={Timer}
              label={option.label}
              description={option.description}
              selected={dailyTime === option.value}
              onClick={() => chooseDuration(option.value)}
            />
          ))}
        </div>
      )}
    </div>
  </section>
)}

        {stage === "complete" && (
          <section className="onboarding-cinematic onboarding-cinematic--complete">
            <div className="onboarding-cinematic__content">
              <h1 className="onboarding-cinematic__title">
                Votre refuge est prêt.
              </h1>

              <p className="onboarding-cinematic__summary">
                {finalCopy.opening}, Harmonauria vous proposera en priorité des
                pauses pour {finalCopy.purpose}. Votre objectif quotidien est de
                <strong> {durationLabel}</strong>, en une ou plusieurs pauses.
              </p>

              <p className="onboarding-cinematic__note">
                Les respirations et les paysages évolueront doucement avec votre
                rythme et vos ressentis.
              </p>

              <button
                type="button"
                className="onboarding-enter"
                onClick={finishOnboarding}
                disabled={isSaving}
                aria-busy={isSaving}
              >
                {isSaving ? "Enregistrement…" : "Entrer dans mon refuge"}
                <ArrowRight size={19} strokeWidth={2.1} aria-hidden="true" />
              </button>
              {saveError && <p role="alert">{saveError}</p>}
            </div>
          </section>
        )}
      </main>

      <style>{`
        .onboarding-fullscreen {
          --ob-ink: #203e3e;
          --ob-copy: #506866;
          --ob-green: #61837a;
          --ob-sage: #76a5a7;
          --ob-pale: #cbe2db;
          --ob-line: rgba(97, 131, 122, 0.16);

          position: fixed;
          inset: 0;
          z-index: 9999;

          width: 100%;
          min-height: 100dvh;

          overflow-y: auto;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;

          background: #ffffff;
          color: var(--ob-ink);
        }

        .onboarding-experience {
          width: 100%;
          min-height: 100dvh;
opacity: 1;
transform: translate3d(0, 0, 0);
backface-visibility: hidden;
-webkit-backface-visibility: hidden;
will-change: opacity, transform;

transition:
  opacity ${TRANSITION_MS}ms
    cubic-bezier(0.22, 1, 0.36, 1),
  transform ${TRANSITION_MS}ms
    cubic-bezier(0.22, 1, 0.36, 1);
        }

        .onboarding-experience.is-leaving {
opacity: 0;
transform: translate3d(0, -6px, 0);
pointer-events: none;
        }

        .onboarding-cinematic {
          width: 100%;
          min-height: 100dvh;
          padding:
            max(32px, env(safe-area-inset-top, 0px))
            max(22px, env(safe-area-inset-right, 0px))
            max(32px, env(safe-area-inset-bottom, 0px))
            max(22px, env(safe-area-inset-left, 0px));

          display: grid;
          place-items: center;
          text-align: center;
          background: #ffffff;
        }

        .onboarding-cinematic__content {
          width: min(100%, 780px);
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .onboarding-cinematic__title {
          max-width: 920px;
          margin: 0;

          color: var(--ob-ink);
          font-family: var(--font-display, "Nunito", sans-serif);
          font-size: clamp(2.45rem, 6vw, 5.25rem);
          font-weight: 800;
          line-height: 1.04;
          letter-spacing: -0.052em;
          text-wrap: balance;
          outline: none;

backface-visibility: hidden;
-webkit-backface-visibility: hidden;
will-change: opacity, transform;

animation:
  onboardingTitleReveal 720ms
    cubic-bezier(0.16, 1, 0.3, 1)
    both;
        }

        .onboarding-cinematic__summary {
          max-width: 720px;
          margin: clamp(30px, 5vw, 46px) auto 0;

          color: var(--ob-copy);
          font-size: clamp(1.05rem, 2vw, 1.28rem);
          font-weight: 500;
          line-height: 1.78;
          text-wrap: balance;

          opacity: 0;
          transform: translateY(18px);
          animation: onboardingCopyReveal 760ms ease 320ms forwards;
        }

        .onboarding-cinematic__summary strong {
          color: var(--ob-ink);
          font-weight: 800;
        }

        .onboarding-cinematic__note {
          max-width: 600px;
          margin: 18px auto 0;

          color: rgba(80, 104, 102, 0.7);
          font-size: 0.92rem;
          font-weight: 550;
          line-height: 1.65;
          text-wrap: balance;

          opacity: 0;
          transform: translateY(14px);
          animation: onboardingCopyReveal 680ms ease 520ms forwards;
        }

        .onboarding-enter {
          min-height: 58px;
          margin-top: clamp(34px, 5vw, 48px);
          padding: 0 27px;

          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;

          border: 0;
          border-radius: 999px;
          background: var(--ob-green);
          box-shadow: 0 16px 34px rgba(97, 131, 122, 0.2);

          color: #ffffff;
          font-family: inherit;
          font-size: 0.98rem;
          font-weight: 800;
          cursor: pointer;

          opacity: 0;
          transform: translateY(14px);
          animation: onboardingCopyReveal 680ms ease 700ms forwards;

          transition:
            transform 190ms ease,
            background 190ms ease,
            box-shadow 190ms ease;
        }

        .onboarding-enter:hover {
          transform: translateY(-2px);
          background: #55776f;
          box-shadow: 0 20px 42px rgba(97, 131, 122, 0.27);
        }

.onboarding-question {
  position: relative;
  width: 100%;
  min-height: 100dvh;
  padding: 0 20px max(40px, calc(env(safe-area-inset-bottom, 0px) + 24px));
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  background: #ffffff;
}

.onboarding-topbar {
  position: absolute;
  top: calc(env(safe-area-inset-top, 0px) + 14px);
  left: 50%;
  width: min(calc(100% - 48px), 980px);
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 18px;
  transform: translateX(-50%);
}

        .onboarding-topbar__side {
          min-width: 0;
          display: flex;
          justify-content: flex-start;
        }

        .onboarding-topbar__side--right {
          justify-content: flex-end;
        }

        .onboarding-topbar__spacer {
          width: 74px;
          height: 42px;
        }

        .onboarding-logo {
          width: auto;
          height: 27px;
          display: block;
          opacity: 0.94;
        }

        .onboarding-back {
          min-height: 42px;
          padding: 0 10px 0 6px;

          display: inline-flex;
          align-items: center;
          gap: 7px;

          border: 0;
          border-radius: 999px;
          background: transparent;
          color: var(--ob-copy);

          font-family: inherit;
          font-size: 0.86rem;
          font-weight: 750;
          cursor: pointer;

          transition:
            color 180ms ease,
            background 180ms ease,
            transform 180ms ease;
        }

        .onboarding-back:hover {
          color: var(--ob-ink);
          background: rgba(203, 226, 219, 0.2);
          transform: translateX(-2px);
        }

        .onboarding-step-label {
          color: rgba(80, 104, 102, 0.68);
          font-size: 0.77rem;
          font-weight: 800;
          letter-spacing: 0.08em;
        }

.onboarding-progress {
  position: absolute;
  top: calc(env(safe-area-inset-top, 0px) + 70px);
  left: 50%;
  width: min(calc(100% - 48px), 980px);
  height: 2px;
  margin: 0;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(97, 131, 122, 0.1);
  transform: translateX(-50%);
}

        .onboarding-progress span {
          display: block;
          height: 100%;
          border-radius: inherit;
          background: var(--ob-green);
          transition: width 520ms cubic-bezier(0.16, 1, 0.3, 1);
        }

.onboarding-page-hero {
  flex: 0 0 auto;
  margin-top: calc(118px + env(safe-area-inset-top, 0px));
}

.onboarding-question__center {
  width: 100%;
  flex: 1 0 auto;
  min-height: 0;
  padding: 24px 0 34px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.onboarding-options {
  width: min(100%, 620px);
  margin: 0;
  display: grid;
  gap: 13px;
}

        .onboarding-options--duration {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .onboarding-option {
          width: 100%;
          min-height: 86px;
          padding: 15px 17px;

          display: grid;
          grid-template-columns: 50px minmax(0, 1fr) 24px;
          align-items: center;
          gap: 14px;

          border: 1px solid var(--ob-line);
          border-radius: 22px;
          background: #ffffff;
          box-shadow: 0 9px 28px rgba(49, 72, 66, 0.045);

          color: var(--ob-ink);
          font-family: inherit;
          text-align: left;
          cursor: pointer;

          transition:
            transform 190ms ease,
            border-color 190ms ease,
            background 190ms ease,
            box-shadow 190ms ease;
        }

        .onboarding-option:hover {
          transform: translateY(-2px);
          border-color: rgba(97, 131, 122, 0.34);
          background: rgba(247, 251, 250, 0.64);
          box-shadow: 0 15px 34px rgba(49, 72, 66, 0.075);
        }

        .onboarding-option.is-selected {
          border-color: rgba(97, 131, 122, 0.42);
          background: rgba(203, 226, 219, 0.18);
        }

        .onboarding-option__icon {
          width: 50px;
          height: 50px;

          display: grid;
          place-items: center;

          border-radius: 16px;
          background: rgba(203, 226, 219, 0.28);
          color: var(--ob-green);
        }

        .onboarding-option__copy {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .onboarding-option__copy strong {
          color: var(--ob-ink);
          font-size: 1rem;
          font-weight: 800;
          line-height: 1.35;
        }

        .onboarding-option__copy span {
          color: var(--ob-copy);
          font-size: 0.88rem;
          font-weight: 500;
          line-height: 1.45;
        }

        .onboarding-option__arrow {
          color: rgba(97, 131, 122, 0.55);
          transition: transform 190ms ease, color 190ms ease;
        }

        .onboarding-option:hover .onboarding-option__arrow {
          color: var(--ob-green);
          transform: translateX(3px);
        }

@keyframes onboardingTitleReveal {
  from {
    opacity: 0;
    transform: translate3d(0, 16px, 0);
  }

  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes onboardingQuestionReveal {
  from {
    opacity: 0;
    transform: translate3d(0, 12px, 0);
  }

  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}


        @keyframes onboardingCopyReveal {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 680px) {

          .onboarding-logo {
            height: 23px;
          }

          .onboarding-back span {
            display: none;
          }

          .onboarding-topbar__spacer {
            width: 42px;
          }

          .onboarding-options--duration {
            grid-template-columns: 1fr;
          }

          .onboarding-option {
            min-height: 79px;
            padding: 13px 14px;
            grid-template-columns: 46px minmax(0, 1fr) 21px;
            gap: 12px;
            border-radius: 20px;
          }

          .onboarding-option__icon {
            width: 46px;
            height: 46px;
            border-radius: 15px;
          }

          .onboarding-option__copy strong {
            font-size: 0.96rem;
          }

          .onboarding-option__copy span {
            font-size: 0.82rem;
          }

          .onboarding-cinematic__title {
            font-size: clamp(2.25rem, 11vw, 3.65rem);
          }

          .onboarding-cinematic__summary {
            margin-top: 30px;
            font-size: 1rem;
            line-height: 1.72;
          }

          .onboarding-cinematic__note {
            font-size: 0.86rem;
          }

          .onboarding-enter {
            width: min(100%, 330px);
          }

.onboarding-question {
  min-height: 100dvh;
  padding-right: 14px;
  padding-bottom: max(28px, calc(env(safe-area-inset-bottom, 0px) + 18px));
  padding-left: 14px;
}

.onboarding-topbar {
  top: calc(env(safe-area-inset-top, 0px) + 18px);
  width: calc(100% - 28px);
}

.onboarding-progress {
  top: calc(env(safe-area-inset-top, 0px) + 76px);
  width: calc(100% - 28px);
}

.onboarding-page-hero {
  margin-top: calc(116px + env(safe-area-inset-top, 0px));
}

.onboarding-question__center {
  width: 100%;
  min-height: 0;
  padding: 18px 0 28px;
  align-items: flex-start;
}

.onboarding-options {
  width: min(100%, 500px);
}
        }

        @media (max-width: 680px) and (max-height: 740px) {
  .onboarding-page-hero {
    margin-top: calc(106px + env(safe-area-inset-top, 0px));
  }

.onboarding-question__center {
  min-height: 0;
  padding-top: 14px;
  align-items: flex-start;
}

  .onboarding-options {
    gap: 10px;
  }

  .onboarding-option {
    min-height: 72px;
  }
}

        @media (hover: none) and (pointer: coarse) {
          .onboarding-option:hover,
          .onboarding-enter:hover,
          .onboarding-back:hover {
            transform: none;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .onboarding-experience,
          .onboarding-cinematic__title,
          .onboarding-cinematic__summary,
          .onboarding-cinematic__note,
          .onboarding-enter,
          .onboarding-progress span,
          .onboarding-option,
          .onboarding-option__arrow,
          .onboarding-back {
            animation: none !important;
            transition: none !important;
          }

          .onboarding-cinematic__title,
          .onboarding-cinematic__summary,
          .onboarding-cinematic__note,
          .onboarding-enter {
            opacity: 1 !important;
            transform: none !important;
            filter: none !important;
          }
        }
      `}</style>
    </div>
  );
}